import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useMainTableContext } from '@insights_module/context/MainTableContext';
import {
  AudioContextualSummary,
  AudioWithDetailsAndGarm,
  Garm,
} from '@insights_module/types/graphql/Schema';
import {
  getEpisodesQueryObject,
  removeShowTitleFromEpisodeTitle,
} from '@insights_module/utils/episodesUtils';
import { useShowDetailsContext } from '@insights_module/context/ShowDetailsContext';
import {
  AQLBrandSafetyGarmQuery,
  AQLBrandSafetyGarmFilterQuery,
} from '@insights_module/adc_ql/queries';
import { GarmOverview, CategoryType } from '@insights_module/types/BrandSafety';
import { getDateNumberDaysAgo } from '@utils/dateUtils';
import { useSelectedShowDetails } from '@insights_module/hooks/useGraphqlShowDetails';
import { graphqlApi } from '@configs/axios-instances';

const PAGE_SIZE = '20';

export const useShowDetailsTableData = () => {
  const queryClient = useQueryClient();
  const { selectedShow } = useInsightsContext();
  const { includeUnprocessed } = useMainTableContext();
  const {
    sort,
    searchTerm,
    showLast90Days,
    selectedShowDetailsGarmCategory,
    selectedShowDetailsGarmRisk,
    selectedShowDetailsEntity,
    selectedShowDetailsCategory,
    setEpisodeRelatedToGarmScoreCount,
    pageNumber,
  } = useShowDetailsContext();

  const [audios, setAudios] = useState<AudioWithDetailsAndGarm[]>([]);
  const [audiosTotalCount, setAudiosTotalCount] = useState(0);

  const { data: show, isLoading: _isShowLoading } = useSelectedShowDetails();

  const garmOverview: GarmOverview = useMemo(() => {
    return show?.garm_brand_safety_summery || ({} as GarmOverview);
  }, [show]);

  function getGarmQueryObj() {
    if (!selectedShowDetailsGarmCategory || !selectedShowDetailsGarmRisk) {
      return null;
    }

    if (Array.isArray(selectedShowDetailsGarmRisk)) {
      if (selectedShowDetailsGarmRisk.length === 1) {
        return new AQLBrandSafetyGarmQuery([
          new AQLBrandSafetyGarmFilterQuery(
            selectedShowDetailsGarmCategory as CategoryType,
            'eq',
            selectedShowDetailsGarmRisk[0]
          ),
        ]);
      } else {
        return new AQLBrandSafetyGarmQuery([
          new AQLBrandSafetyGarmFilterQuery(
            selectedShowDetailsGarmCategory as CategoryType,
            'range',
            {
              gte: selectedShowDetailsGarmRisk[0],
              lte: selectedShowDetailsGarmRisk[selectedShowDetailsGarmRisk.length - 1],
            }
          ),
        ]);
      }
    } else {
      return new AQLBrandSafetyGarmQuery([
        new AQLBrandSafetyGarmFilterQuery(
          selectedShowDetailsGarmCategory as CategoryType,
          'eq',
          selectedShowDetailsGarmRisk
        ),
      ]);
    }
  }

  const episodesQueryObject = useMemo(() => {
    return getEpisodesQueryObject(
      selectedShow,
      {
        global_search: searchTerm,
        ...getGarmQueryObj(),
        linked_named_entities: selectedShowDetailsEntity?.id,
        contextual: selectedShowDetailsCategory,
        published_date: showLast90Days
          ? {
              gte: getDateNumberDaysAgo(
                90,
                (show?.processed_audios_details?.most_recently_processed_audio_date as string) ||
                  new Date()
              ),
            }
          : null,
      },
      includeUnprocessed
    );
  }, [
    show,
    searchTerm,
    selectedShow,
    showLast90Days,
    includeUnprocessed,
    selectedShowDetailsGarmCategory,
    selectedShowDetailsGarmRisk,
    selectedShowDetailsEntity,
    selectedShowDetailsCategory,
  ]);

  const variables = useMemo(
    () => ({
      sortField: sort.sort_field,
      sortOrder: sort.sort_order,
      pageNo: pageNumber,
      pageSize: PAGE_SIZE,
      payload: JSON.stringify({ query: episodesQueryObject }),
    }),
    [episodesQueryObject, sort.sort_field, sort.sort_order, pageNumber]
  );

  const {
    data,
    isLoading,
    isFetching,
    isPreviousData,
    remove: clearDataCache,
    ...props
  } = useQuery({
    queryKey: ['show-details-table-data', variables],
    queryFn: async () =>
      graphqlApi.post('', {
        query: audiosWithDetailsGarmIabContextualAndEntities,
        variables,
      }),
    enabled: !!selectedShow,
  });

  useEffect(() => {
    if (data) {
      setAudios(data.data.audios.data);
      setAudiosTotalCount(data.data.audios.pagination.total);
    }
  }, [data]);

  useEffect(() => {
    // If we have more data we will try to prefetch the next page.
    if (!data?.data?.audios?.pagination?.total) {
      return;
    }

    const hasMore =
      Number(PAGE_SIZE) * Number(variables.pageNo) < data?.data.audios?.pagination?.total;

    const prefetchVariables = { ...variables, pageNo: String(Number(variables.pageNo) + 1) };

    if (!isPreviousData && hasMore) {
      queryClient.prefetchQuery({
        queryKey: ['graphql', 'graphql-library-audios', prefetchVariables],
        queryFn: async () =>
          graphqlApi.post('', {
            query: audiosWithDetailsGarmIabContextualAndEntities,
            variables: prefetchVariables,
          }),
      });
    }
  }, [data, isPreviousData, variables, queryClient]);

  const _audios = useMemo(() => {
    return audios.map((episode: AudioWithDetailsAndGarm) => {
      const { id, title, duration, published_date, created_at } = episode;
      return {
        details: {
          id,
          title: removeShowTitleFromEpisodeTitle(title, show?.name || ''),
          duration,
          coverImage:
            episode.details?.coverImage ||
            episode.details?.groups?.[0]?.thumbnail_url ||
            show?.thumbnail_url,
        },
        publishDate: new Date(published_date),
        processDate: new Date(created_at),
        garm: episode.garm,
        contextual: episode.contextual,
        linked_named_entities: episode.linked_named_entities,
      };
    });
  }, [selectedShow, audios, isLoading]);

  const getGarmByEpisodeId = useCallback(
    (id: string) => {
      const episode = _audios.find((item) => item.details.id === id);

      if (episode) {
        return {
          id: episode.details.id,
          created_at: episode.processDate,
          results: Object.entries(episode?.garm || ({} as Garm)).map(([key, value]) => {
            return {
              category: key,
              risk: value.toLowerCase(),
            };
          }),
        };
      }

      return { results: undefined, isLoading: false };
    },
    [_audios]
  );

  const {
    iabData,
    appleData,
  }: { iabData: AudioContextualSummary[]; appleData: AudioContextualSummary[] } = useMemo(() => {
    if (!show?.contextual) return { iabData: [], appleData: [] };

    return show?.contextual?.reduce(
      (
        acc: { iabData: AudioContextualSummary[]; appleData: AudioContextualSummary[] },
        item: AudioContextualSummary
      ) => {
        if (item.taxonomy[0]?.taxonomy === 'iab') {
          acc.iabData.push(item);
        }
        if (item.taxonomy[0]?.taxonomy === 'apple') {
          acc.appleData.push(item);
        }
        return acc;
      },
      { iabData: [], appleData: [] }
    );
  }, [show]);

  const entities = useMemo(() => show?.linked_named_entities, [show]);

  const getEpisodeIndex = (id: string): number => {
    return audios.findIndex((audio) => audio?.id === id);
  };

  const getEpisodeByIndex = (index: number) => {
    return audios[index];
  };
  const getEpisodeById = (id: string) => {
    return audios.find((episode) => episode.id === id);
  };

  const getPrevEpisode = (id: string) => {
    const index = getEpisodeIndex(id);
    return getEpisodeByIndex(index - 1);
  };

  const getNextEpisode = (id: string) => {
    const index = getEpisodeIndex(id);
    return getEpisodeByIndex(index + 1);
  };

  useEffect(() => {
    setEpisodeRelatedToGarmScoreCount(show?.episodes_related_to_garm_score_count || 0);
  }, [show]);

  return {
    show,
    episodes: _audios,
    isLoading: _isShowLoading,
    episodesTotalCount: audiosTotalCount,
    isEpisodesLoading: isLoading,
    isFetchingNextDataPage: isFetching,
    garmOverview,
    entities,
    contextual: { iabData, appleData },
    clearDataCache,
    getEpisodeById,
    getGarmByEpisodeId,
    getPrevEpisode,
    getEpisodeByIndex,
    getNextEpisode,
    getEpisodeIndex,
    ...props,
  };
};

const audiosWithDetailsGarmIabContextualAndEntities = `query Audios($pageSize: String, $pageNo: String, $sortField: String, $sortOrder: String, $payload: String) {
  audios(page_size: $pageSize, page_no: $pageNo, sort_field: $sortField, sort_order: $sortOrder, payload: $payload) {
    data {
      id
      title
      duration
      client_duration
      original_url
      hosted_url
      language
      skip_processing
      external_ids {
        name
        value
      }
      published_date
      created_at
      updated_at
      details {
        id
        title
        duration
        client_duration
        original_url
        hosted_url
        language
        skip_processing
        groups {
          id
          name
          type
          thumbnail_url
          tenant_id
          audio_count
          most_recently_published_audio_date
          extra_fields {
            name
            value
          }
          podcast_metadata {
            apple_categories
            apple_rating_count
            apple_star_rating
            author
            description
            podcast_type
          }
          created_at
          updated_at
        }
        published_date
        created_at
        updated_at
      }
      garm {
        adult_explicit_content
        arms_ammunition
        crime_harmful_act
        death_injury_military_conflict
        debated_social
        illegal_drugs
        hate_speech
        obscenity
        online_piracy
        spam_harmful
        terrorism
      }
      contextual {
        id
        name
        display_name
        score
        taxonomy {
          taxonomy
          name
          tier
          id
          parent_id
        }
      }
      linked_named_entities {
        id
        type
        value
        references {
          value
          timestamp
          timestamps
          count
        }
        referencesCount
      }
    }
    pagination {
      total
      page_no
      page_size
      page_token
    }
  }
}`;
